import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, OnDestroy } from '@angular/core';
import { TrainingOccurrenceDTO } from '../../user-training-plan/models/training-occurrence';
import { UserTrainingPlanService } from '../../user-training-plan/services/user-training-plan.service';
import { Subscription, forkJoin, of } from 'rxjs';
import { CommonService } from '../../../shared/services/common.service';
import { SharedConstants } from '../../../../app/shared/constants/shared-constants';
import { MessageService } from 'primeng/api';
import { MySpaceConstants } from '../../my-space-constants';
import { PanelEventHub } from '../../../shared-components/panel-hub/models/panel-event-hub';
import { PanelHubConfiguration } from '../../../shared-components/panel-hub/models/panel-hub-configuration';
import { Features } from '../../../shared-components/panel-hub/enums/features.enum';
import { PanelHubConstants } from '../../../shared-components/panel-hub/constants/panel-hub-constants';
import { PaginationConfigByFeature } from '../../../shared-components/panel-hub/constants/feature-pagination-configuration';
import { TrainingPlanQueryResult } from '../../user-training-plan/models/training-plan-query-result';
import { AppliedFilters } from '../../../shared-components/panel-hub/models/applied-filters';
import { PaginationState } from '../../../shared-components/panel-hub/models/pagination-state';
import { SortCriteria } from '../../../shared-components/panel-hub/models/sort-criteria';
import { PanelHubStateChangedEvent } from '../../../shared-components/panel-hub/models/panel-hub-state-changed-event';
import { PanelHubEventType } from '../../../shared-components/panel-hub/enums/panel-hub-eventtype';
import { FilterConfig } from '../../../shared-components/panel-hub/models/filter-config';
import { SortField } from '../../../shared-components/panel-hub/models/sort-field';
import { toOptions } from '../../../shared/utils';
import { SearchMapperService } from '../../../shared/services/search-mapper.service';
import { TrainingPlanSearchRequest } from '../../user-training-plan/models/training-plan-search-request';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { EmployeesTrainingPlanService } from '../../employees-training-plan/services/employees-training-plan.service';


@Component({
  selector: 'app-training-plan-panel-list',
  templateUrl: './training-plan-panel-list.component.html',
  styleUrls: ['./training-plan-panel-list.component.scss']
})
export class TrainingPlanPanelListComponent implements OnInit, OnChanges, OnDestroy {
  // Configuration Inputs
  @Input() puid: string | null = null;
  @Input() enableSelection: boolean = false;
  @Input() enableShowAll: boolean = true;

  // Outputs
  @Output() selectionChanged = new EventEmitter<{
    selectedItems: any[];
    deselectedItems: any[];
    isSelectAllAcrossPages: boolean;
    currentFilters: AppliedFilters | null;
    currentSort: SortCriteria | null;
    currentPagination: PaginationState | null;
    isShowAll: boolean;
  }>();

  // PanelHub Integration
  panelEventHub!: PanelEventHub;
  panelHubConfig: PanelHubConfiguration = {
    title: '',
    feature: Features.TrainingPlanView,
    standardFilterCount: PanelHubConstants.StandardFilterLength,
    showSelectAll: false,
    enableShowAllToggle: true,
    enableSelectAll: false,
    enableSelection: false,
    selectionIdField: 'number',
    showAllLabel: 'Show all',
    showAllTooltip: MySpaceConstants.ShowAll_InformationIconMessage,
    enableToggling: true,
    enablePanelCardClick: false,
    enablePanelHighLight: false,
    enablePanelCardView: false,
    enableExport: true,
    enableExpandCollapse: true,
    enableViewModes: false,
    enableAssignmentView: false,
    enableWhereAmIUsedView: false,
    paginationConfig: PaginationConfigByFeature[Features.TrainingPlanView]
  };

  pagedData: TrainingPlanQueryResult = { totalRecords: 0, data: [], pageNumber: 1, pageSize: 25 };
  currentFilters: AppliedFilters | null = null;
  currentPagination: PaginationState | null = null;
  currentSort: SortCriteria | null = null;
  isShowAll: boolean = false;
  isTrainingPlanDataAvailable: boolean = false;

  // Selection tracking (for manager view)
  selectedItemIds: Set<string> = new Set();
  deselectedItemIds: Set<string> = new Set();
  selectAllActive: boolean = false;

  // Legacy modal support
  trainingOccurrences: TrainingOccurrenceDTO[] = [];
  subscription: Subscription | undefined;
  noData: boolean = false;
  informationIconTooltip: string = MySpaceConstants.ShowAll_InformationIconMessage;
  isTrainingRequirementHistoryModelOpen = false;
  isSessionDetailsModalOpen: boolean = false;
  sessionIdsLinkedWithCourseTrainingOccurrence: string[] = [];
  showLoader: boolean = false;

  constructor(
    private userTrainingPlanService: UserTrainingPlanService,
    private employeesTrainingPlanService: EmployeesTrainingPlanService,
    private commonService: CommonService,
    private messageService: MessageService,
    private searchMapper: SearchMapperService
  ) {}

  ngOnInit(): void {
    // Only load data if:
    // - puid is null (user's own training plan)
    // - puid is a non-empty string (manager viewing specific employee)
    // Do NOT load if puid is empty string (manager with no employee selected)
    const shouldLoadData = this.puid === null || (this.puid !== null && this.puid !== '');
    
    if (shouldLoadData) {
      this.isTrainingPlanDataAvailable = true;
      this.initializePanelHub();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When puid changes, reload data accordingly
    if (changes['puid'] && !changes['puid'].firstChange) {
      const newPuid = changes['puid'].currentValue;
      
      // Load data if puid is null or a non-empty string
      if (newPuid === null || (newPuid !== null && newPuid !== '')) {
        this.isTrainingPlanDataAvailable = true;
        
        // Reset all state when user changes
        this.currentFilters = null;
        this.currentSort = null;
        this.isShowAll = false;
        this.resetPaginationState();
        
        // If already initialized, reset panel hub and refresh data
        if (this.panelEventHub) {
          this.panelEventHub.resetConfigurations(); // This resets text search, filters, and selection
          this.panelEventHub.resetShowAll(); // Reset the Show All checkbox
          this.refreshPanelHubData();
        } else {
          // First time loading with a valid puid
          this.initializePanelHub();
        }
      } else {
        // puid is empty string, clear data and hide panel
        this.isTrainingPlanDataAvailable = false;
        this.pagedData = { totalRecords: 0, data: [], pageNumber: 1, pageSize: 25 };
      }
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onPanelEventHubInitialized(hub: PanelEventHub) {
    this.panelEventHub = hub;
  }

  onPanelHubStateChanged(event: PanelHubStateChangedEvent) {
    switch (event.type) {
      case PanelHubEventType.FilterChanged:
        this.currentFilters = event.state.filters || null;
        if (this.currentPagination) this.currentPagination.pageNumber = 1;
        this.refreshPanelHubData();
        break;

      case PanelHubEventType.SortChanged:
        this.currentSort = event.state.sort || null;
        this.refreshPanelHubData();
        break;

      case PanelHubEventType.PaginationChanged:
        this.currentPagination = event.state.pagination || null;
        this.refreshPanelHubData();
        break;

      case PanelHubEventType.SelectAllToggled:
      case PanelHubEventType.ItemSelectionChanged:
        this.handleSelectionChanged(event);
        break;

      case PanelHubEventType.ShowAllChanged:
        this.isShowAll = event.state.isShowAllChanged || false;
        this.resetPanelConfiguration();
        this.resetPaginationState();
        this.refreshPanelHubData();
        break;
    }
  }

  private initializePanelHub() {
    this.showLoader = true;
    this.panelHubConfig.enableSelection = this.enableSelection;
    this.panelHubConfig.enableSelectAll = this.enableSelection;
    this.panelHubConfig.showSelectAll = this.enableSelection;
    this.panelHubConfig.enableShowAllToggle = this.enableShowAll;

    forkJoin({
      lookup: this.userTrainingPlanService.getTrainingPlanLookup()
    }).pipe(
      switchMap(results => {
        const filterConfigs = this.buildFilterConfigs(results.lookup);
        const sortOptions = this.buildSortOptions();
        this.setPanelHubConfigExtensions(filterConfigs, sortOptions);
        return this.fetchInitialPagedData().pipe(
          map(paged => ({ paged }))
        );
      }),
      catchError(err => {
        this.handleInitError();
        return of(null);
      }),
      finalize(() => {
        this.showLoader = false;
      })
    ).subscribe(result => {
      if (!result) return;
      this.applyPagedResult(result.paged);
    });
  }

  private buildFilterConfigs(lookup: any): FilterConfig[] {
    const typeOptions = [...toOptions(lookup.types, 'name', 'name')];
    const trainingItemGroupOptions = [...toOptions(lookup.trainingItemGroups, 'name', 'code')];
    const curriculumOptions = [...toOptions(lookup.curriculums, 'name', 'value')];
    const coreOrElectiveOptions = [...toOptions(lookup.coreOrElective, 'name', 'name')];
    const statusOptions = [...toOptions(lookup.statuses, 'name', 'name')];
    const trainingStatusOptions = [...toOptions(lookup.trainingStatuses, 'name', 'name')];
    
    // Manager Update is a boolean field - create Yes/No options manually
    const managerUpdateOptions = [
      { name: 'Yes', value: 'true' },
      { name: 'No', value: 'false' }
    ];

    return [
      { displayName: 'Type', fieldName: 'Type', options: typeOptions },
      { displayName: 'Training Item Group', fieldName: 'TrainingItemGroup', options: trainingItemGroupOptions },
      { displayName: 'Curriculum', fieldName: 'Curriculum', options: curriculumOptions },
      { displayName: 'Core/Elective', fieldName: 'CoreOrElective', options: coreOrElectiveOptions },
      { displayName: 'Status', fieldName: 'Status', options: statusOptions },
      { displayName: 'Training', fieldName: 'TrainingStatus', options: trainingStatusOptions },
      { displayName: 'Manager Update', fieldName: 'ManagerUpdate', options: managerUpdateOptions }
    ].filter(fc => fc.options.length > 0);
  }

  private buildSortOptions(): SortField[] {
    return [
      { fieldDisplayName: 'Type', fieldName: 'Type' },
      { fieldDisplayName: 'Core/Elective', fieldName: 'CoreOrElective' },
      { fieldDisplayName: 'Status', fieldName: 'Status' },
      { fieldDisplayName: 'Training', fieldName: 'TrainingStatus' }
    ];
  }

  private setPanelHubConfigExtensions(filterConfigs: FilterConfig[], sortOptions: SortField[]) {
    this.panelHubConfig = { ...this.panelHubConfig, filterConfigs, sortOptions };
  }

  private fetchInitialPagedData() {
    const paginationState: PaginationState = {
      pageNumber: this.panelHubConfig.paginationConfig.startPage,
      pageSize: this.panelHubConfig.paginationConfig.defaultPageSize,
      totalRecords: 0
    };
    return this.queryTrainingPlanData(paginationState, null, null, false);
  }

  private queryTrainingPlanData(
    pagination: PaginationState,
    filters: AppliedFilters | null,
    sort: SortCriteria | null,
    isShowAll: boolean
  ) {
    const searchRequest: TrainingPlanSearchRequest = this.searchMapper.mapToBaseSearchRequest(
      filters,
      sort,
      pagination
    ) as TrainingPlanSearchRequest;
    
    searchRequest.puid = this.puid;
    searchRequest.isShowAll = isShowAll;

    return this.userTrainingPlanService.searchTrainingPlan(searchRequest);
  }

  private refreshPanelHubData() {
    if (!this.currentPagination) {
      this.currentPagination = {
        pageNumber: this.panelHubConfig.paginationConfig.startPage,
        pageSize: this.panelHubConfig.paginationConfig.defaultPageSize,
        totalRecords: 0
      };
    }
    this.fetchPagedData();
  }

  private fetchPagedData() {
    if (!this.currentPagination) return;
    this.panelEventHub.startLoader();
    this.queryTrainingPlanData(
      this.currentPagination,
      this.currentFilters,
      this.currentSort,
      this.isShowAll
    ).pipe(
      map(result => ({ result })),
      catchError(err => {
        this.pagedData = { totalRecords: 0, data: [], pageNumber: 1, pageSize: 0 };
        this.noData = true;
        this.messageService.add({
          severity: SharedConstants.Severity_Error,
          summary: SharedConstants.Audit_Fail_Error_Message,
          sticky: true
        });
        return of(null);
      }),
      finalize(() => {
        this.panelEventHub.stopLoader();
      })
    ).subscribe(res => {
      if (!res) return;
      this.applyQueryResult(res.result);
    });
  }

  private resetPanelConfiguration() {
    this.resetPaginationState();
    this.currentFilters = null;
    this.currentSort = null;
    if (this.panelEventHub) {
      this.panelEventHub.resetConfigurations();
    }
    this.pagedData.pageSize = this.panelHubConfig.paginationConfig.defaultPageSize;
  }

  private resetPaginationState() {
    this.currentPagination = {
      pageNumber: this.panelHubConfig.paginationConfig.startPage,
      pageSize: this.panelHubConfig.paginationConfig.defaultPageSize,
      totalRecords: 0
    };
      const defaultPageSize = this.panelHubConfig?.paginationConfig?.defaultPageSize || 25;
      this.pagedData = {
        data: [],
        totalRecords: 0,
        pageNumber: 1,
        pageSize: defaultPageSize
      };
  }

  private applyPagedResult(result: TrainingPlanQueryResult) {
    // Extract trainingPlanDetails array from nested data object
    const dataArray = (result.data as any)?.trainingPlanDetails || [];
    this.pagedData = {
      totalRecords: result.totalRecords,
      data: dataArray,
      pageNumber: result.pageNumber ?? 1,
      pageSize: result.pageSize ?? 25
    };
    this.noData = dataArray.length === 0;
    this.currentPagination = {
      pageNumber: this.panelHubConfig.paginationConfig.startPage,
      pageSize: this.panelHubConfig.paginationConfig.defaultPageSize,
      totalRecords: result.totalRecords
    };
    
    // Emit progressKPI if available (check both camelCase and PascalCase)
    const progressKPI = result.progressKPI || (result as any).ProgressKPI;
    if (progressKPI) {
      if (this.puid) {
        // Employee view - use employeesTrainingPlanService
        this.employeesTrainingPlanService.updateProgressKPI(progressKPI);
      } else {
        // User view - use userTrainingPlanService
        this.userTrainingPlanService.updateProgressKPI(progressKPI);
      }
    }
  }

  private applyQueryResult(result: TrainingPlanQueryResult) {
    // Extract trainingPlanDetails array from nested data object
    const dataArray = (result.data as any)?.trainingPlanDetails || [];
    this.pagedData = {
      totalRecords: result.totalRecords,
      data: dataArray,
      pageNumber: result.pageNumber ?? 1,
      pageSize: result.pageSize ?? 25
    };
    this.noData = dataArray.length === 0;
    if (this.currentPagination) this.currentPagination.totalRecords = result.totalRecords;
    
    // Emit progressKPI if available (check both camelCase and PascalCase)
    const progressKPI = result.progressKPI || (result as any).ProgressKPI;
    if (progressKPI) {
      if (this.puid) {
        // Employee view - use employeesTrainingPlanService
        this.employeesTrainingPlanService.updateProgressKPI(progressKPI);
      } else {
        // User view - use userTrainingPlanService
        this.userTrainingPlanService.updateProgressKPI(progressKPI);
      }
    }
  }

  private handleInitError() {
    this.isTrainingPlanDataAvailable = false;
    this.showLoader = false;
    this.pagedData = { totalRecords: 0, data: [], pageNumber: 1, pageSize: 25 };
    this.noData = true;
    this.messageService.add({
      severity: SharedConstants.Severity_Error,
      summary: SharedConstants.Audit_Fail_Error_Message,
      sticky: true
    });
  }

  private handleSelectionChanged(event: PanelHubStateChangedEvent) {
    if (!this.enableSelection) return;
    
    // Emit comprehensive selection state for pagination-aware bulk actions
    this.selectionChanged.emit({
      selectedItems: event.state.selectedItems || [],
      deselectedItems: event.state.deselectedItems || [],
      isSelectAllAcrossPages: event.state.isSelectAllAcrossPages || false,
      currentFilters: this.currentFilters,
      currentSort: this.currentSort,
      currentPagination: this.currentPagination,
      isShowAll: this.isShowAll
    });
  }

  public refreshPanel() {
    this.refreshPanelHubData();
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Pending':
        return 'status-orange';
      case 'Complete':
        return 'status-green';
        case 'Active':
        return 'status-green';
      case 'Cancelled':
      case 'Removed':
      case 'Revoked':
      case 'N/A':
        return 'status-gray';
      case 'Locked':
        return 'status-red';
      default:
        return '';
    }
  }
  
  fetchTrainingOccurrences(number: string) {
    this.isTrainingRequirementHistoryModelOpen = false;
    this.subscription = this.userTrainingPlanService.getTrainingRequirementHistoryOnId(number, this.puid)
      .subscribe({
        next: (response: TrainingOccurrenceDTO[]) => {
          this.isTrainingRequirementHistoryModelOpen = true;
          this.trainingOccurrences = response.map(occurrence => {
            if (occurrence.type === 'Document' || occurrence.type === 'Course') {
              return {
                ...occurrence,
                title: occurrence.title
              };
            }
            return occurrence;
          });
        },
        error: () => {
          this.messageService.add({
            severity: SharedConstants.Severity_Error,
            summary: SharedConstants.Audit_Fail_Error_Message,
            sticky: true
          });
        }
      });
  }

  openDocument(documentNumber: string, version: string) {
    var url = location.origin.concat("/eqms.documentDetails/", documentNumber, ":", version);
    window.open(url, "_blank");
  }

  openCourse(courseNumber: string) {
    var url = location.origin.concat("/training/eqms.courses/", courseNumber);
    window.open(url, "_blank");
  }

  openSessionDetailsModal = (sessionIds: string[] | null) => {
    if (sessionIds && sessionIds.length > 0) {
      this.sessionIdsLinkedWithCourseTrainingOccurrence = sessionIds;
      this.isSessionDetailsModalOpen = true;
    }
  }

  closeSessionDetailsModal = (isModalShow: boolean) => {
    this.isSessionDetailsModalOpen = isModalShow;
  }

  onExportClicked() {
    // Build export request using same structure as search
    const exportRequest: TrainingPlanSearchRequest = {
      Filter: this.currentFilters ? {
        SearchText: this.currentFilters.searchText || null,
        FilterItems: (this.currentFilters.filters || []).map(f => ({
          Field: f.field,
          Values: f.values.map(v => String(v)),
          Type: f.type
        }))
      } : null,
      Sort: this.currentSort ? {
        Fields: [{
          field: this.currentSort.field,
          sortDirection: this.currentSort.sortDirection,
          type: this.currentSort.type
        }]
      } : null,
      Pagination: { PageNumber: 1, PageSize: 10000 }, // Export all (up to max)
      puid: this.puid,
      isShowAll: this.isShowAll
    };

    this.userTrainingPlanService.exportTrainingPlan(exportRequest)
      .subscribe({
        next: (response) => {
          const byteCharacters = atob(response.excelInBytes);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          });
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          const fileName = `TrainingPlan_${response.puid}_${this.commonService.date_TO_String(new Date())}.xlsx`;
          anchor.download = fileName;
          anchor.href = url;
          anchor.click();
          window.URL.revokeObjectURL(url);
        },
        error: () => {
          this.messageService.add({
            severity: SharedConstants.Severity_Error,
            summary: 'Export failed',
            sticky: true
          });
        }
      });
  }
}
